import CorrelationMetric from '@/types/correlation-metric';
import Dictionary from '@/types/dictionary';
import GrowthTypes from '@/types/growth-types';
import LocalizedString from '@/types/localized-string';
import PercentGrowth from '@/types/percent-growth';
import RiskMetric from '@/types/risk-metric';
import ModalSize from '@/types/modal-size';
import ComparisonItemType from '@/types/comparison-item-type';
import SortDirection from '@/types/sort-direction';
import countries from 'i18n-iso-countries';

export enum SHARED_CONTENT_TYPES {
  PROPOSAL = 'PROPOSAL',
  COMPARISON = 'COMPARISON',
}

export enum SHARED_CONTENT_METHOD {
  LINK = 'LINK',
  EMAIL = 'EMAIL',
}

export enum TIME_PERIODS {
  '1Y' = '1Y',
  '2Y' = '2Y',
  '3Y' = '3Y',
  '4Y' = '4Y',
  '5Y' = '5Y',
  '10Y' = '10Y',
  'INC' = 'INC',
  '6M' = '6M',
  '3M' = '3M',
  '1M' = '1M',
  '1W' = '1W',
  '1D' = '1D',
  'YT' = 'YT',
  'MT' = 'MT',
  'QT' = 'QT',
  'CI' = 'CI',
}

export enum RISK_PERIODS {
  '1Y' = '1Y',
  '3Y' = '3Y',
  '5Y' = '5Y',
  '10Y' = '10Y',
  'INC' = 'INC',
  'YT' = 'YT',
  'CI' = 'CI',
}

export const PERIODS_VALUE_MT = -1;
export const PERIODS_VALUE_CI = -9999;
export const PERIODS_VALUE_INC = null;

export enum TIME_RANGE_PRESET {
  MULTI_ITEMS_DEFAULT = 'MULTI_ITEMS_DEFAULT',
  SINGLE_ITEM_DEFAULT = 'SINGLE_ITEM_DEFAULT',
  RISK_DEFAULT = 'RISK_DEFAULT',
  RISK_REWARD_DEFAULT = 'RISK_REWARD_DEFAULT',
  CALENDAR_YEAR_DEFAULT = 'CALENDAR_YEAR_DEFAULT',
  PORTFOLIO_CORRELATION_MATRIX = 'PORTFOLIO_CORRELATION_MATRIX',
  INCOME_STREAM = 'INCOME_STREAM',
  BMO_GIF = 'BMO_GIF',
}

export enum PERFORMANCE_CHART {
  GROWTH = 'growth',
  RISK_REWARD = 'riskReward',
  DRAWDOWN = 'drawdown',
  INCOME = 'income',
  FUND_GROWTH = 'fundGrowth',
  PORTFOLIO_GROWTH = 'portfolioGrowth',
  GROUP_GROWTH = 'groupGrowth',
}

export enum ComparisonToolbarKeys {
  GROWTH = 'growth',
  FUND_GROWTH = 'fundGrowth',
  PORTFOLIO_GROWTH = 'portfolioGrowth',
  GROUP_GROWTH = 'groupGrowth',
  RISK_REWARD = 'riskReward',
  DRAWDOWN = 'drawdown',
  INCOME = 'income',
  RISK_METRICS = 'riskMetrics',
  FUND_RISK_PERIOD = 'fundRiskPeriod',
  PORTFOLIO_RISK_PERIOD = 'portfolioRiskPeriod',
  GROUP_RISK_PERIOD = 'groupRiskPeriod',
  ADVANCED_RISK_METRICS = 'advancedRiskMetrics',
  CALENDAR_YEAR_RETURNS = 'calendarYearReturns',
  FUND_CALENDAR_YEAR_PERIOD = 'fundCalendarYearPeriod',
  PORTFOLIO_CALENDAR_YEAR_PERIOD = 'portfolioCalendarYearPeriod',
  GROUP_CALENDAR_YEAR_PERIOD = 'groupCalendarYearPeriod',
  CORRELATION_MATRICES = 'correlationMatrices',
  COMPARISON_CORRELATION_MATRIX = 'comparisonCorrelationMatrix',
}

export const MAX_CALENDAR_RETURNS = 10;

// Number of months in specific periods
export const PERIOD_MONTHS: Dictionary<number> = {
  '1Y': 12,
  '2Y': 24,
  '3Y': 36,
  '5Y': 60,
  '4Y': 48,
  '10Y': 120,
  '6M': 6,
  '3M': 3,
  '1M': 1,
};

// Number of years in specific periods
export const PERIOD_YEARS: Dictionary<number> = {
  '1Y': 1,
  '2Y': 2,
  '3Y': 3,
  '4Y': 4,
  '5Y': 5,
  '10Y': 10,
};

// Disclaimer relevance time limits
export const DISCLAIMER_RELEVANCE_YEARS = 10;

export enum NAV_PATHS {
  MODELS = '/models',
  ENTERPRISE_MODELS = '/models/enterprise',
  CUSTOM_BENCHMARKS = '/models/custom-benchmarks',
  SEARCH = '/search',
  CONTACTS = '/contacts',
  ADMIN = '/admin',
  CONTACT_DETAILS = '/contacts/:contactId',
  CONTACT_PORTFOLIOS = '/contacts/:contactId/portfolios',
  CONTACT_COMPARISONS = '/contacts/:contactId/comparisons',
  COMPARISONS = '/comparisons',
  COMPARISON = '/comparison',
  COMPARISON_DETAILS = '/comparison/:comparisonId',
  HOUSEHOLDS = '/households',
  HOUSEHOLD_DETAILS = '/households/:householdId',
  HOUSEHOLD_PORTFOLIOS = '/households/:householdId/portfolios',
  HOUSEHOLD_COMPARISONS = '/households/:householdId/comparisons',
  HOUSEHOLD_CONTACT_DETAILS = '/households/:householdId/contacts/:contactId',
  MESSAGES = '/messages',
  AUTH = '/auth',
  ERROR = '/error',
  INSIGHTS = '/insights',
  PLANNING = '/planning',
  PLANNING_ASSIGN_CONTACT = '/planning/:planId/assign-contact',
  PLANNING_ERROR = '/planning/error/:errorKey',
  PLANNING_IMPORT_ACCOUNTS = '/planning/:planId/import-accounts',
  PROPOSAL = '/proposal',
  SETTINGS = '/settings',
  OAUTH2_SUCCESS = '/oauth2-success',
  OAUTH2 = '/oauth2',
  ANNOUNCEMENTS = '/announcements',
  SSO_COMPLETE = '/sso-complete',
}

export enum COMPARISON_NAV_PATHS {
  PERFORMANCE = '/performance',
  RISK = '/risk',
  INCOME = '/income',
  MARKUP = '/markup',
  GIF = '/bmo-gif',
  ESG = '/esg',
  HISTORICAL_SCENARIOS = '/historical-scenarios',
  TAX_ANALYSIS = '/tax-analysis',
}

export enum KEY_CODES {
  ENTER = 'Enter',
  ESCAPE = 'Escape',
  MINUS = '-',
}

export const DEFAULT_GROWTH_VALUES: PercentGrowth = Object.keys(TIME_PERIODS).reduce((acc, key) => {
  acc[key as GrowthTypes] = null;
  return acc;
}, {} as PercentGrowth);

export const DEFAULT_CORRELATION_VALUES: CorrelationMetric = {
  '1Y': [],
  '3Y': [],
  '5Y': [],
  '10Y': [],
  YT: [],
  INC: [],
  CI: [],
};

/**
 * Default options for correlation calculations.
 *
 * @property {boolean} useLongestHistory - Indicates whether to use the longest available history for correlation.
 * @property {undefined} commonInception - The common inception date for correlation, if any.
 * @property {boolean} useBaseCurrency - Indicates whether to use the base currency for correlation.
 */
export const DEFAULT_CORRELATION_OPTIONS = {
  useLongestHistory: false,
  commonInception: undefined,
  useBaseCurrency: false,
};

export const SORTING_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as {
  ASC: SortDirection;
  DESC: SortDirection;
};

/**
 * Default options for start time and end time for rolling calculations.
 *
 * @property {boolean} useLongestHistory - Indicates whether to use the longest available history for correlation.
 * @property {undefined} commonInception - The common inception date for correlation, if any.
 * @property {boolean} useBaseCurrency - Indicates whether to use the base currency for correlation.
 */
export const DEFAULT_TIMESTAMPS_ROLLING_OPTIONS = {
  useLongestHistory: false,
  commonInception: undefined,
  useBaseCurrency: false,
};

export const UNIT_BASE = 8;

export const MAX_ACTIVE_CONVERSATIONS = 3;

// Should always be equal to the $comparison-column-width SCSS variable.
export const COMPARISON_CARD_WIDTH = 320;

// Should always be equal to the $comparison-column-width-small SCSS variable.
export const COMPARISON_CARD_WIDTH_SMALL = 250;

// Height of cells to calculate merged block height
export const COMPARISON_CELL_HEIGHT = 40;

export const PERIOD_LIST = ['YT', '6M', '1Y', '3Y', '5Y', '10Y'];

export enum COMPARISON_CATEGORIES {
  KEY_METRICS = 'keyMetrics',
  PERFORMANCE = 'performance',
  CALENDAR_YEAR_RETURNS = 'calendarYearReturns',
  FUND_CALENDAR_YEAR_PERIOD = 'fundCalendarYearPeriod',
  PORTFOLIO_CALENDAR_YEAR_PERIOD = 'portfolioCalendarYearPeriod',
  GROUP_CALENDAR_YEAR_PERIOD = 'groupCalendarYearPeriod',
  ASSET_DETAILS = 'assetDetails',
  INDUSTRY_HOLDINGS = 'industryHoldings',
  RISK_METRICS = 'riskMetrics',
  FUND_RISK_PERIOD = 'fundRiskPeriod',
  PORTFOLIO_RISK_PERIOD = 'portfolioRiskPeriod',
  GROUP_RISK_PERIOD = 'groupRiskPeriod',
  ADVANCED_RISK_METRICS = 'advancedRiskMetrics',
  LIPPER_LEADER_SCORES = 'lipperLeaderScores',
  INVESTMENT_DETAILS = 'investmentDetails',
  TOP_HOLDINGS = 'topHoldings',
  COUNTRY_ALLOCATIONS = 'countryAllocations',
  EQUITY_METRICS = 'equityMetrics',
  EQUITY_STYLE_BREAKDOWN = 'equityStyleBreakdown',
  FIXED_INCOME_METRICS = 'fixedIncomeMetrics',
  REGIONS = 'fixedIncomeRegion',
  FIXED_INCOME_SECTORS = 'fixedIncomeSectors',
  FIXED_INCOME_STYLE = 'fixedIncomeStyle',
  CREDIT_QUALITY = 'creditQuality',
  MATURITY_ALLOCATIONS = 'maturityAllocations',
  ESG_OVERVIEW = 'esgOverview',
  ESG_RATING_DISTRIBUTION = 'esgRatingDistribution',
  ESG_IMPLIED_TEMPERATURE_RISE = 'esgImpliedTemperatureRise',
  ALTERNATIVE_INVESTMENT = 'alternativeInvestment',
  FEE_INFORMATION = 'feeInformation',
  ALTERNATIVE_PERFORMANCE = 'alternativePerformance',
}

export enum MER_FIELDS {
  MER = 'mer',
  DISCRETIONARY_MER = 'discretionaryMer',
  TOTAL_MER = 'totalMer',
}

export const DISABLED_MER_FIELDS = [MER_FIELDS.DISCRETIONARY_MER, MER_FIELDS.TOTAL_MER] as string[];

export const ACCOUNT_TYPES: Dictionary<string> = {
  TFSA: 'TFSA',
  REGISTERED: 'Registered',
  NON_REGISTERED: 'Non-Registered',
};

export const REGISTERED_ACCOUNT_TYPES: Dictionary<string> = {
  RIF: 'RIF',
  LIF: 'LIF',
  LRIF: 'LRIF',
  PRIF: 'PRIF',
  RLIF: 'RLIF',
  RSP: 'RSP',
  LIRA: 'LIRA',
  LRSP: 'LRSP',
  RLSP: 'RLSP',
};

export const PROVINCES: Dictionary<string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  FEDERAL: 'Federal',
  MB: 'Manitoba',
  NT: 'Northwest Territories',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

export const FREQUENCIES: Dictionary<string> = {
  WEEKLY: 'Weekly',
  BI_WEEKLY: 'Bi-weekly',
  MONTHLY: 'Monthly',
  SEMI_ANNUALLY: 'Semi-annually',
  ANNUALLY: 'Annually',
  QUARTERLY: 'Quarterly',
};

export enum CREDIT_QUALITY_CATEGORIES {
  AAA = 'AAA',
  AA = 'AA',
  A = 'A',
  BBB = 'BBB',
  BB = 'BB',
  B = 'B',
  CCC = 'CCC',
  CC = 'CC',
  C = 'C',
  D = 'D',
  BELOW_B = 'BELOW_B',
  NOT_RATED = 'Not Rated',
}

// Used for weighted average rating
export enum CREDIT_QUALITY_RATINGS {
  AAA = 10,
  AA = 9,
  A = 8,
  BBB = 7,
  BB = 6,
  B = 5,
  CCC = 4,
  CC = 3,
  C = 2,
  D = 1,
}

export const CREDIT_QUALITY_BELOW_B_CATEGORIES: string[] = [
  CREDIT_QUALITY_CATEGORIES.CCC,
  CREDIT_QUALITY_CATEGORIES.CC,
  CREDIT_QUALITY_CATEGORIES.C,
  CREDIT_QUALITY_CATEGORIES.D,
];

export const DISPLAYED_CREDIT_QUALITY_CATEGORIES = [
  CREDIT_QUALITY_CATEGORIES.AAA,
  CREDIT_QUALITY_CATEGORIES.AA,
  CREDIT_QUALITY_CATEGORIES.A,
  CREDIT_QUALITY_CATEGORIES.BBB,
  CREDIT_QUALITY_CATEGORIES.BB,
  CREDIT_QUALITY_CATEGORIES.B,
  CREDIT_QUALITY_CATEGORIES.BELOW_B,
  CREDIT_QUALITY_CATEGORIES.NOT_RATED,
];

export enum MATURITY_CATEGORIES {
  '0-1' = 'Less than 1 Year',
  '1-3' = '1 to 3 Years',
  '3-5' = '3 to 5 Years',
  '5-10' = '5 to 10 Years',
  '10-20' = '10 to 20 Years',
  '20-30' = '20 to 30 Years',
  '30+' = 'Greater than 30 Years',
  'Other' = 'Other',
}

export enum MATURITY_CATEGORIES_FACTSET {
  '0-1' = 'Less than 1 Year',
  '1-3' = '1 to 3 Years',
  '3-5' = '3 to 5 Years',
  '5-10' = '5 to 10 Years',
  '10-20' = '10 to 20 Years',
  '20+' = 'Greater than 20 Years',
  'Other' = 'Other',
}

export enum REGIONS {
  NORTH_AMERICA = 'NORTH_AMERICA',
  GREATER_EUROPE = 'GREATER_EUROPE',
  GREATER_ASIA = 'GREATER_ASIA',
  LATIN_AMERICA = 'LATIN_AMERICA',
  AFRICA_MIDDLE_EAST = 'AFRICA_MIDDLE_EAST',
  OTHER = 'OTHER',
}

export const FIXED_INCOME_SECTORS: Dictionary<string> = {
  CAN_CORP_BONDS: 'Canadian Corporate Bonds',
  CAN_GOV_BONDS: 'Canadian Government Bonds',
  CAN_BONDS_OTHER: 'Canadian Bonds - Other',
  FOR_CORP_BONDS: 'International Corporate Bonds',
  FOR_GOV_BONDS: 'International Government Bonds',
  FOR_BONDS_OTHER: 'International Bonds - Other',
  CASH: 'Cash',
};

export const FIXED_INCOME_SECTORS_US: Dictionary<string> = {
  GOVERMENT: 'Government',
  MUNICIPAL: 'Municipal',
  SECURITIZED: 'Securitized',
  CORPORATE: 'Corporate',
  CASH: 'Cash',
  OTHER: 'Other',
};

export const FIXED_INCOME_SECTOR_GROUPS: Dictionary<string> = {
  GOVERNMENT: 'Government',
  CORPORATE: 'Corporate',
  CASH: 'Cash',
  OTHER: 'Other',
};

export enum HOLDINGS_EDITOR_TYPE {
  PORTFOLIO = 'PORTFOLIO',
  BENCHMARK = 'BENCHMARK',
}

export enum ASSET_TYPE {
  EQUITY = 'Equity',
  MIXED_ASSETS = 'Mixed Assets',
  MONEY_MARKET = 'Money Market',
  BOND = 'Bond',
  ALTERNATIVES = 'Alternatives',
  COMMODITY = 'Commodity',
  REAL_ESTATE = 'Real Estate',
  OTHER = 'Other',
  M = 'M',
}

export enum FUND_SOURCE {
  FUNDATA = 'FUNDATA',
  TRKD = 'TRKD',
}

export enum ADVANCED_SEARCH_OPTIONS {
  FUNDS = 'funds',
  BENCHMARKS = 'benchmarks',
  PROXIES = 'proxies',
  STOCKS = 'stocks',
  BONDS = 'bonds',
}

export const SCREENER_TAB_MODES = {
  URL: 'url',
  CONTROLLED: 'controlled',
} as const;

export enum FUND_SEARCH_TYPES {
  FUND = 'FUND',
  BENCHMARK_PROXY = 'BENCHMARK_PROXY',
}

export enum PROXY_OPTION {
  EQUITY = 'EQUITY',
  FIXED_INCOME = 'FIXED_INCOME',
  OTHER = 'OTHER',
}

export enum QUICK_ADD_ITEM_KEYS {
  PROXIES = 'PROXIES',
  CASH = 'CASH',
}

export enum COMPARISON_ITEM_TYPES {
  ALT = 'alt',
  FUND = 'fund',
  PORTFOLIO = 'portfolio',
  STOCK = 'stock',
  BENCHMARK = 'benchmark',
  GROUP = 'group',
}

export const SINCE_INCEPTION_ITEM_TYPES: ComparisonItemType[] = [
  COMPARISON_ITEM_TYPES.FUND,
  COMPARISON_ITEM_TYPES.STOCK,
  COMPARISON_ITEM_TYPES.PORTFOLIO,
  COMPARISON_ITEM_TYPES.GROUP,
];

export const QUICK_SEARCH_SUBTYPES = Object.freeze({
  BENCHMARK_PORTFOLIO: 'BENCHMARK_PORTFOLIOS',
});

export enum EQUITY_STYLE_CATEGORIES {
  GROWTH = 'Growth',
  BLEND = 'Blend',
  VALUE = 'Value',
  OTHER = 'Other',
}

export enum CAPITALIZATION_CATEGORIES {
  LARGE = 'Large',
  MEDIUM = 'Mid',
  SMALL = 'Small',
  OTHER = 'Other',
}

export enum FIXED_INCOME_DURATION_CATEGORIES {
  LONG = 'Long',
  MEDIUM = 'Medium',
  SHORT = 'Short',
}

export enum FIXED_INCOME_CREDIT_RATING_CATEGORIES {
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
}

export enum RISK_METRICS_KEYS {
  R_SQUARED = 'rSquared',
  INFO_RATIO = 'infoRatio',
  STANDARD_DEVIATION = 'standardDeviation',
  BETA = 'beta',
  DOWNSIDE = 'downside',
  UPSIDE = 'upside',
  SORTINO = 'sortino',
  MAXIMUM_DRAWDOWN = 'maximumDrawdown',
  CORRELATION_COEFFICIENT = 'correlationCoefficient',
  SHARPE_RATIO = 'sharpeRatio',
  TRACKING_ERROR = 'trackingError',
  ALPHA = 'alpha',
}

export const ASSET_ALLOCATION_GROUPS = {
  CASH: 'CASH',
  EQUITY: 'EQUITY',
  FIXED_INCOME: 'FIXED_INCOME',
  OTHER: 'OTHER',
  ALTERNATIVE_INVESTMENT: 'ALTERNATIVE_INVESTMENT',
};

export const ASSET_GROUPS = {
  EQUITY: 'EQUITY',
  FIXED_INCOME: 'FIXED_INCOME',
  OTHER: 'OTHER',
  CASH: 'CASH',
  ALTERNATIVE_INVESTMENT: 'ALTERNATIVE_INVESTMENT',
  MIXED_ASSETS: 'MIXED_ASSETS',
  REAL_ASSETS: 'REAL_ASSETS',
};

export const HOLDING_TYPES = {
  FUND: 'FUNDS',
  STOCK: 'STOCKS',
};

export const STOCK_ASSET_ALLOCATIONS: Dictionary<LocalizedString> = {
  CAN: { 'en-CA': 'Canadian Equity', 'fr-CA': 'Actions canadiennes' },
  USA: { 'en-CA': 'United States Equity', 'fr-CA': 'Actions américaines' },
  OTHER: { 'en-CA': 'Foreign Equity', 'fr-CA': 'Actions internationales' },
};

export const STOCK_REGION_ALLOCATIONS: Dictionary<string> = {
  CAN: REGIONS.NORTH_AMERICA,
  USA: REGIONS.NORTH_AMERICA,
  FRA: REGIONS.GREATER_EUROPE,
  GBR: REGIONS.GREATER_EUROPE,
  GER: REGIONS.GREATER_EUROPE,
  AUS: REGIONS.OTHER,
  CHN: REGIONS.GREATER_ASIA,
  JPN: REGIONS.GREATER_ASIA,
  HKG: REGIONS.GREATER_ASIA,
  IND: REGIONS.GREATER_ASIA,
  ARE: REGIONS.AFRICA_MIDDLE_EAST,
  NLD: REGIONS.GREATER_EUROPE,
  OTHER: REGIONS.OTHER,
};

export const COUNTRY_CODES = Object.keys(countries.getAlpha3Codes());
export const UNIDENTIFIED_COUNTRY = 'Unidentified';

export const DEFAULT_RISK_VALUES: RiskMetric = {
  '1Y': null,
  '3Y': null,
  '5Y': null,
  '10Y': null,
  YT: null,
  INC: null,
  CI: null,
};

export enum Interval {
  Monthly = 'monthly',
  Weekly = 'weekly',
}

export enum PersistenceAuthType {
  OAUTH2 = 'OAUTH2',
  NO_AUTH = 'NO_AUTH',
}

export enum YieldParamsType {
  GIC = 'GIC',
  HISA = 'HISA',
  MLGIC = 'MLGIC',
  MLGIC_PPN = 'MLGIC/PPN',
}

export enum BOOLEAN_OPERATOR {
  OR = 'OR',
  AND = 'AND',
  NOT = 'NOT',
}

export const CHART_STARTING_CAPITAL = 100000;

export const BOND_TAG = 'BOND|';

export const CSV_TEMPLATE_HEADERS = {
  'en-CA': ['exchange', 'identifier', 'units'],
  'fr-CA': ['bourse', 'identifiant', 'unités'],
};

export const CSV_TAX_TEMPLATE_HEADERS = {
  'en-CA': ['exchange', 'identifier', 'units', 'cost basis', 'purchase date'],
  'fr-CA': ['bourse', 'identifiant', 'unités', 'coût d’acquisition', 'date d’achat'],
};

export const CSV_HEADER_NORMALIZATION_MAP: Record<string, string> = {
  exchange: 'exchange',
  identifier: 'identifier',
  units: 'units',
  'cost basis': 'costBasis',
  'purchase date': 'purchaseDate',
  bourse: 'exchange',
  identifiant: 'identifier',
  unités: 'units',
  'coût d’acquisition': 'costBasis',
  'date d’achat': 'purchaseDate',
};

// Define canonical header identifiers (internal keys)
export enum CsvHeaderKey {
  EXCHANGE = 'exchange',
  IDENTIFIER = 'identifier',
  UNITS = 'units',
  PURCHASE_DATE = 'purchaseDate',
  COST_BASIS = 'costBasis',
}

// Map display headers (with format hints) to canonical keys
export const CSV_HEADER_TO_KEY_MAP: Record<string, CsvHeaderKey> = {
  exchange: CsvHeaderKey.EXCHANGE,
  identifier: CsvHeaderKey.IDENTIFIER,
  units: CsvHeaderKey.UNITS,
  'purchase date': CsvHeaderKey.PURCHASE_DATE,
  'cost basis': CsvHeaderKey.COST_BASIS,
  bourse: CsvHeaderKey.EXCHANGE,
  identifiant: CsvHeaderKey.IDENTIFIER,
  unités: CsvHeaderKey.UNITS,
  "date d'achat": CsvHeaderKey.PURCHASE_DATE,
  "coût d'acquisition": CsvHeaderKey.COST_BASIS,
};

// Define which headers indicate a tax template
export const TAX_TEMPLATE_REQUIRED_KEYS = new Set([CsvHeaderKey.COST_BASIS, CsvHeaderKey.PURCHASE_DATE]);

export const MODAL_SIZES: Dictionary<ModalSize> = {
  SM: 'sm',
  LG: 'lg',
  XL: 'xl',
};

export enum FILE_EXTENSIONS {
  XLSX = '.xlsx',
  CSV = '.csv',
}

export enum MIME {
  TEXT_CSV = 'text/csv',
  APPLICATION_VND_MS_EXCEL = 'application/vnd.ms-excel',
}

export const FILE_EXTENSIONS_MIME: Dictionary<string> = {
  [FILE_EXTENSIONS.XLSX]: MIME.APPLICATION_VND_MS_EXCEL,
  [FILE_EXTENSIONS.CSV]: MIME.TEXT_CSV,
};

export const FILE_UPLOAD_SPECS: { maxSize: number; multiple: boolean; supportedFileExtensions: FILE_EXTENSIONS[] } = {
  maxSize: 150000000,
  multiple: false,
  supportedFileExtensions: [FILE_EXTENSIONS.XLSX],
};

export const HOLDING_NAME_MAX_LENGTH = 22;
export const MAX_EXPANDED_ITEMS_IN_PORTFOLIO = 10;
export const MAX_VISIBLE_COMPARISONS = 10;
export const MAX_LOADED_COMPARISONS = 20;

export enum PortfolioFlag {
  AcceptedPortfolio = 'acceptedPortfolio',
  UnallocatedPortfolio = 'unallocatedPortfolio',
}

export enum ComparisonFlagsType {
  SHARED_WITH_CLIENT = 'SharedWithClient',
}

export const ENTER_KEY = 'Enter';

export const SUBALLOCATION_ORDER = [
  'US Cash & Cash Equivalents',
  'Non US Cash & Cash Equivalents',
  'US Government Taxable Fixed Income',
  'US Tax Exempt Fixed Income',
  'US Tax Exempt High Yield Fixed Income',
  'US Corporate Investment Grade Fixed Income',
  'US Corporate High Yield Fixed Income',
  'International Fixed Income',
  'International High Yield Fixed Income',
  'Emerging Markets Fixed Income',
  'Preferred',
  'Convertible',
  'Global Fixed Income',
  'Multi-Sector Fixed Income',
  'Alternative Fixed Income, Long/Short',
  'Alternative Fixed Income, Event Driven',
  'Alternative Fixed Income, Currency',
  'Alternative Fixed Income, Private Debt',
  'Alternative Fixed Income, PPVA Annuity',
  'US Large Cap Core Equity',
  'US Large Cap Value Equity',
  'US Large Cap Growth Equity',
  'Sector',
  'US Mid Cap Core Equity',
  'US Mid Cap Value Equity',
  'US Mid Cap Growth Equity',
  'US Small Cap Core Equity',
  'US Small Cap Value Equity',
  'US Small Cap Growth Equity',
  'International Equity',
  'Emerging Markets Equity',
  'Global Equity',
  'Alternative Equity, Long/Short',
  'Alternative Equity, Event Driven',
  'Alternative Equity, Exchange Fund',
  'Alternative Equity, Private Equity',
  'Alternative Equity, PPVA Annuity',
  'Alternative Equity, Market Neutral',
  'Other Equity',
  'Real Assets, Listed Real Estate',
  'Real Assets, Private Real Estate',
  'Real Assets, Commodity Securities',
  'Real Assets, Commodity Physicals',
  'Real Assets, Commodity Futures',
  'Real Assets, Resource Equity',
  'Real Assets, Private Natural Resources',
  'Real Assets, Master Limited Partnership',
  'Real Assets, Listed Infrastructure',
  'Real Assets, Private Infrastructure',
  'Real Assets, Other',
  'Mixed Assets, Fixed Income Only',
  'Mixed Assets, 80% Fixed Income, 20% Equity',
  'Mixed Assets, 60% Fixed Income, 40% Equity',
  'Mixed Assets, 40% Fixed Income, 60% Equity',
  'Mixed Assets, 20% Fixed Income, 80% Equity',
  'Mixed Assets, Equity Only',
  'Mixed Assets, Tactical',
  'Other Alternatives, Managed Futures',
  'Other Alternatives, Multi-Strategy',
  'Other Alternatives, Trading/Inverse/Leveraged',
  'Other Alternatives, Volatility',
  'Other Alternatives, PPVA Annuity',
  'Other Alternatives, Client Directed/Other',
  'Unclassified',
];
